import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";

const truncateText = (text, maxLength = 13) => {
  if (!text) return "";
  if (text?.length < maxLength) return text;
  const newText = text
    ?.toString()
    ?.split(" ")
    ?.filter((word) => word?.length > 0)
    ?.map((word) => word?.charAt(0))
    ?.join("")
    ?.slice(0, 4);

  return newText;
};

const AreaChart = ({
  categories,
  data,
  colors,
  yTitle = "",
  useGradient,
  tooltipFormatter,
  tooltipTitleFormatter,
  height,
  chartType = "bar",
  showNameLabel = false,
  showMarkers = false,
  showFirstChar = false,
  showDataLabels = false,
  customToolTip = null,
}) => {
  const [options, setOptions] = useState({
    chart: {
      type: chartType,
      toolbar: {
        show: false,
      },
      height: "100%",
      zoom: {
        enabled: false,
      },
    },
    legend: {
      show: true,
    },
    xaxis: {
      categories: [],
    },
    yaxis: {
      title: {
        text: yTitle,
      },
      labels: {
        formatter: (val) => val,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 0,
        dataLabels: {
          position: "top",
        },
      },
    },
    grid: {
      show: true,
      borderColor: "#56577A",
      strokeDashArray: 5,
      position: "back",
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
    },
    dataLabels: {
      enabled: showDataLabels,
      offsetY: 0,
      style: {
        fontSize: "10px",
        colors: ["#2F3A79"],
      },
    },
    colors: colors,
    tooltip: {
      enabled: true,
      theme: "dark",
      x: {
        formatter: (val) => val,
      },
      y: {
        formatter: tooltipFormatter,
        title: {
          formatter: (seriesName) => {
            const safeName = seriesName || "";
            return tooltipTitleFormatter ? tooltipTitleFormatter(safeName) : safeName;
          },
        },
      },
      style: {
        fontSize: "10px",
      },
      marker: {
        show: false,
      },
      x: {
        show: showNameLabel,
      },
      position: "left",
    },
    fill: useGradient
      ? {
          type: "gradient",
          gradient: {
            shade: "light",
            type: "vertical",
            shadeIntensity: 0.3,
            gradientToColors: ["#fff"],
            inverseColors: false,
            opacityFrom: 0.9,
            opacityTo: 0.2,
            stops: [0, 100],
          },
        }
      : { type: "solid" },
  });

  const customTooltipFormatter = ({ series, seriesIndex, dataPointIndex, w }) => {
    const obj = customToolTip[dataPointIndex];
    if (!obj) return "";
    const categoryLabel = categories[dataPointIndex] || "";
    const rows = Object.entries(obj)
      .map(
        ([key, val]) =>
          `<p style="margin: 0;font-size:12px;text-transform:capitalize; padding:0 5px 5px 5px;">${key} : ${val}</p>`
      )
      .join("");
    return `
    <div style="padding:0px;">
      <p style="margin: 0 0 5px 0; font-size: 10px; padding:5px; background:#000">${categoryLabel}</p>
      ${rows}
    </div>
  `;
  };

  useEffect(() => {
    if (!data || data.length === 0) return;
    const hasValueEqualToZero = data?.every((ele) => ele?.data?.every((item) => item <= 0));
    setOptions((prev) => ({
      ...prev,
      xaxis: {
        categories: categories,
        labels: {
          ...(showFirstChar ? { formatter: (val) => truncateText(val) } : { formatter: (val) => val }),
          // rotate: -45,
          // trim: false,
          hideOverlappingLabels: false,
          showDuplicates: true,
        },
        tooltip: {
          enabled: false,
        },
      },
      yaxis: {
        ...(hasValueEqualToZero && { max: 5, min: 0 }),
        title: {
          text: yTitle,
        },
        categories: categories,
        labels: {
          formatter: (val) => val,
        },
      },
      tooltip: {
        enabled: true,
        // shared: false,
        // intersect: true,
        theme: "dark",
        ...(customToolTip
          ? { custom: customTooltipFormatter }
          : {
              x: {
                formatter: (val, opts) => {
                  return categories[opts.dataPointIndex] || val;
                },
              },
            }),
      },
      ...(showMarkers && {
        markers: {
          size: 4,
          colors: undefined,
          strokeColors: "#fff",
          strokeWidth: 2,
          fillOpacity: 1,
          shape: "circle",
          hover: {
            size: 6,
          },
        },
      }),
      colors: colors,
      fill: useGradient
        ? {
            type: "gradient",
            gradient: {
              shade: "light",
              type: "vertical",
              shadeIntensity: 0.3,
              gradientToColors: ["#fff"],
              inverseColors: false,
              opacityFrom: 0.9,
              opacityTo: 0.2,
              stops: [0, 100],
            },
          }
        : { type: "solid" },
    }));
  }, [categories, data, yTitle, colors, useGradient, customToolTip]);

  return (
    <div className="chartWrapper">
      <Chart options={options} series={data} type={chartType} width="100%" height="100%" />
    </div>
  );
};

export default AreaChart;
